import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import {
  ChevronRight,
  FileText,
  Folder,
  Pencil,
  Plus,
  Save,
  Trash2,
} from 'lucide-react';
import { tutorialAPI } from '@/utils/tutorialApi';
import { buildManualNodePath, IPE_MANUAL_HOME_PATH } from '@/config/ipeRoutes';
import { canManageTutorialNode } from '@/pages/tutorial/utils/tutorialManualPermissions';
import TutorialMarkdown, { extractHeadings } from './components/TutorialMarkdown';
import TutorialMarkdownEditor from './components/TutorialMarkdownEditor';
import TutorialConfirmDialog from './components/TutorialConfirmDialog';
import { TUTORIAL_LINKABLE_NAV_OPTIONS, tutorialNavLabel } from './constants/tutorialNavOptions';

function LinkedNavField({ value, onChange }) {
  return (
    <label>
      关联工作台模块
      <select value={value || ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">不关联</option>
        {TUTORIAL_LINKABLE_NAV_OPTIONS.map((item) => (
          <option key={item.value} value={item.value}>{item.label}</option>
        ))}
      </select>
    </label>
  );
}

function useTutorialImageUpload() {
  const [uploadingImage, setUploadingImage] = useState(false);
  const uploadImage = useCallback(async (file) => {
    setUploadingImage(true);
    try {
      return await tutorialAPI.uploadImage(file);
    } finally {
      setUploadingImage(false);
    }
  }, []);
  return { uploadingImage, uploadImage };
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function PageOutline({ headings }) {
  if (!headings?.length) return null;
  return (
    <aside className="tutorial-outline">
      <div className="tutorial-outline__title">本篇目录</div>
      <ul>
        {headings.map((item) => (
          <li key={item.id} className={`tutorial-outline__item tutorial-outline__item--h${item.level}`}>
            <a href={`#${item.id}`}>{item.text}</a>
          </li>
        ))}
      </ul>
    </aside>
  );
}

function FolderListView({ node, user, isEditor, isManager, onRefresh }) {
  const navigate = useNavigate();
  const children = node.children || [];
  const [editOpen, setEditOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({
    title: '',
    nodeType: 'ARTICLE',
    contentMd: '',
    summary: '',
    linkedNavId: '',
  });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const { uploadingImage, uploadImage } = useTutorialImageUpload();

  const canManageItem = (item) => canManageTutorialNode(item, user, { isManager });
  const isArticleForm = form.nodeType === 'ARTICLE' || editingItem?.nodeType === 'ARTICLE';
  const createModalTitle = form.nodeType === 'FOLDER' ? '新建文件夹' : '新建文档';

  const openCreateModal = () => {
    setEditingItem(null);
    setForm({ title: '', nodeType: 'ARTICLE', contentMd: '', summary: '', linkedNavId: '' });
    setEditOpen(true);
  };

  const closeEditModal = () => {
    if (saving) return;
    setEditOpen(false);
    setEditingItem(null);
    setForm({ title: '', nodeType: 'ARTICLE', contentMd: '', summary: '', linkedNavId: '' });
  };

  useEffect(() => {
    if (!editOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [editOpen]);

  const openEditModal = (item) => {
    setEditingItem(item);
    setForm({
      title: item.title || '',
      nodeType: item.nodeType || 'ARTICLE',
      contentMd: item.contentMd || '',
      summary: item.summary || '',
      linkedNavId: item.linkedNavId || '',
    });
    setEditOpen(true);
  };

  const handleEditItem = (item) => {
    if (item.nodeType === 'FOLDER') {
      openEditModal(item);
      return;
    }
    navigate(buildManualNodePath(item.slug), { state: { startEditing: true } });
  };

  const handleDeleteItem = (item) => {
    setDeleteError('');
    setDeleteTarget(item);
  };

  const closeDeleteDialog = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteError('');
  };

  const confirmDeleteItem = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await tutorialAPI.deleteNode(deleteTarget.id);
      setDeleteTarget(null);
      onRefresh?.();
    } catch (err) {
      setDeleteError(err.message || '删除失败');
    } finally {
      setDeleting(false);
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      window.alert('请填写标题');
      return;
    }
    setSaving(true);
    try {
      if (editingItem) {
        await tutorialAPI.updateNode(editingItem.id, {
          slug: editingItem.slug,
          title: form.title.trim(),
          nodeType: editingItem.nodeType,
          parentId: node.id,
          contentMd: form.contentMd,
          summary: form.summary,
          linkedNavId: form.linkedNavId || null,
          published: true,
        });
      } else {
        await tutorialAPI.createNode({
          title: form.title.trim(),
          nodeType: form.nodeType,
          parentId: node.id,
          contentMd: form.contentMd,
          summary: form.summary,
          linkedNavId: form.linkedNavId || null,
          published: true,
        });
      }
      setEditOpen(false);
      setEditingItem(null);
      setForm({ title: '', nodeType: 'ARTICLE', contentMd: '', summary: '', linkedNavId: '' });
      onRefresh?.();
    } catch (err) {
      window.alert(err.message || (editingItem ? '保存失败' : '创建失败'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tutorial-page">
      <div className="tutorial-page__breadcrumbs">
        {(node.breadcrumbs || []).map((crumb, index) => (
          <React.Fragment key={crumb.slug}>
            {index > 0 ? <ChevronRight size={14} /> : null}
            <Link to={buildManualNodePath(crumb.slug)}>{crumb.title}</Link>
          </React.Fragment>
        ))}
      </div>

      <div className="tutorial-page__head">
        <div>
          <h1>{node.title}</h1>
          <p className="tutorial-page__meta">最近修改：{formatDate(node.updatedAt)}</p>
        </div>
        {isEditor ? (
          <button type="button" className="tutorial-manual__primary-btn" onClick={openCreateModal}>
            <Plus size={15} />
            新建
          </button>
        ) : null}
      </div>

      <div className="tutorial-folder-table">
        <div className="tutorial-folder-table__head">
          <span>名称</span>
          <span>负责人</span>
          <span>修改时间</span>
          <span>操作</span>
        </div>
        {children.length === 0 ? (
          <div className="tutorial-folder-table__empty">该分类下暂无文档</div>
        ) : (
          children.map((item) => (
            <div key={item.id} className="tutorial-folder-table__row">
              <Link to={buildManualNodePath(item.slug)} className="tutorial-folder-table__name">
                {item.nodeType === 'FOLDER' ? <Folder size={16} /> : <FileText size={16} />}
                <span>{item.title}</span>
              </Link>
              <span>{item.ownerName || 'IPE 官方'}</span>
              <span>{formatDate(item.updatedAt)}</span>
              <span className="tutorial-folder-table__actions">
                {canManageItem(item) ? (
                  <>
                    <button
                      type="button"
                      className="tutorial-folder-table__action"
                      onClick={() => handleEditItem(item)}
                    >
                      编辑
                    </button>
                    <button
                      type="button"
                      className="tutorial-folder-table__action tutorial-folder-table__action--danger"
                      onClick={() => handleDeleteItem(item)}
                    >
                      删除
                    </button>
                  </>
                ) : (
                  <span className="tutorial-folder-table__action-placeholder">—</span>
                )}
              </span>
            </div>
          ))
        )}
      </div>

      {editOpen ? (
        <div className="tutorial-editor-modal" role="presentation">
          <div
            className={`tutorial-editor-modal__panel ${isArticleForm ? 'tutorial-editor-modal__panel--large' : 'tutorial-editor-modal__panel--folder'}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="tutorial-editor-modal-title"
          >
            <h3 id="tutorial-editor-modal-title">{editingItem ? '编辑内容' : createModalTitle}</h3>
            <div className="tutorial-editor-modal__body">
              <div className="tutorial-editor-modal__fields">
                <div className="tutorial-editor-modal__fields-row">
                  <label>
                    标题
                    <input
                      value={form.title}
                      onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                    />
                  </label>
                  <label>
                    类型
                    <select
                      value={form.nodeType}
                      disabled={Boolean(editingItem)}
                      onChange={(e) => setForm((prev) => ({ ...prev, nodeType: e.target.value }))}
                    >
                      <option value="ARTICLE">文档</option>
                      <option value="FOLDER">文件夹</option>
                    </select>
                  </label>
                </div>
                {form.nodeType === 'ARTICLE' || editingItem?.nodeType === 'ARTICLE' ? (
                  <div className="tutorial-editor-modal__fields-row">
                    <LinkedNavField
                      value={form.linkedNavId}
                      onChange={(linkedNavId) => setForm((prev) => ({ ...prev, linkedNavId }))}
                    />
                    <label>
                      摘要
                      <input
                        value={form.summary}
                        onChange={(e) => setForm((prev) => ({ ...prev, summary: e.target.value }))}
                      />
                    </label>
                  </div>
                ) : null}
              </div>
              {form.nodeType === 'ARTICLE' || editingItem?.nodeType === 'ARTICLE' ? (
                <div className="tutorial-editor-modal__content">
                  <div className="tutorial-editor-modal__content-label">正文</div>
                  <TutorialMarkdownEditor
                    value={form.contentMd}
                    onChange={(contentMd) => setForm((prev) => ({ ...prev, contentMd }))}
                    onUploadImage={uploadImage}
                    uploadingImage={uploadingImage}
                    defaultMode="edit"
                    minRows={12}
                  />
                </div>
              ) : null}
            </div>
            <div className="tutorial-editor-modal__actions">
              <button type="button" className="tutorial-manual__ghost-btn" disabled={saving} onClick={closeEditModal}>
                取消
              </button>
              <button type="button" className="tutorial-manual__primary-btn" disabled={saving} onClick={handleSave}>
                {saving ? '保存中…' : (editingItem ? '保存' : '创建')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <TutorialConfirmDialog
        open={Boolean(deleteTarget)}
        title="确认删除"
        message={deleteTarget ? `确定删除「${deleteTarget.title}」吗？此操作不可恢复。` : ''}
        confirming={deleting}
        error={deleteError}
        onCancel={closeDeleteDialog}
        onConfirm={confirmDeleteItem}
      />
    </div>
  );
}

function ArticleView({ node, user, isEditor, isManager, onRefresh }) {
  const navigate = useNavigate();
  const location = useLocation();
  const canManage = canManageTutorialNode(node, user, { isManager });
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    title: node.title || '',
    summary: node.summary || '',
    contentMd: node.contentMd || '',
    linkedNavId: node.linkedNavId || '',
  });
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const { uploadingImage, uploadImage } = useTutorialImageUpload();

  const headings = useMemo(
    () => extractHeadings(editing ? form.contentMd : node.contentMd),
    [editing, form.contentMd, node.contentMd],
  );

  useEffect(() => {
    setForm({
      title: node.title || '',
      summary: node.summary || '',
      contentMd: node.contentMd || '',
      linkedNavId: node.linkedNavId || '',
    });
  }, [node]);

  useEffect(() => {
    if (location.state?.startEditing && canManage) {
      setEditing(true);
    }
  }, [location.state?.startEditing, canManage]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await tutorialAPI.updateNode(node.id, {
        slug: node.slug,
        title: form.title.trim(),
        summary: form.summary,
        contentMd: form.contentMd,
        linkedNavId: form.linkedNavId || null,
        nodeType: 'ARTICLE',
        parentId: node.parentId,
        published: true,
      });
      setEditing(false);
      onRefresh?.();
    } catch (err) {
      window.alert(err.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteDialog = () => {
    setDeleteError('');
    setDeleteOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleting) return;
    setDeleteOpen(false);
    setDeleteError('');
  };

  const confirmDelete = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      await tutorialAPI.deleteNode(node.id);
      setDeleteOpen(false);
      navigate(IPE_MANUAL_HOME_PATH);
      onRefresh?.();
    } catch (err) {
      setDeleteError(err.message || '删除失败');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className={`tutorial-page tutorial-page--article${editing ? ' tutorial-page--editing' : ''}`}>
      <div className="tutorial-page__article-shell">
        <div className="tutorial-page__article-main">
          {!editing ? (
            <div className="tutorial-page__breadcrumbs">
              {(node.breadcrumbs || []).map((crumb, index) => (
                <React.Fragment key={crumb.slug}>
                  {index > 0 ? <ChevronRight size={14} /> : null}
                  <Link to={buildManualNodePath(crumb.slug)}>{crumb.title}</Link>
                </React.Fragment>
              ))}
            </div>
          ) : null}

          <div className={`tutorial-page__head${editing ? ' tutorial-page__head--compact' : ''}`}>
            {!editing ? (
              <div>
                <h1>{node.title}</h1>
                <p className="tutorial-page__meta">
                  {node.ownerName || 'IPE 官方'} · 最近修改 {formatDate(node.updatedAt)}
                  {node.linkedNavId ? ` · 关联模块：${tutorialNavLabel(node.linkedNavId)}` : ''}
                </p>
              </div>
            ) : (
              <div className="tutorial-page__head-compact-title">编辑文档</div>
            )}
            {canManage ? (
              <div className="tutorial-page__editor-actions">
                {editing ? (
                  <button
                    type="button"
                    className="tutorial-manual__primary-btn"
                    disabled={saving}
                    onClick={handleSave}
                  >
                    <Save size={15} />
                    {saving ? '保存中…' : '保存'}
                  </button>
                ) : (
                  <button type="button" className="tutorial-manual__ghost-btn" onClick={() => setEditing(true)}>
                    <Pencil size={15} />
                    编辑
                  </button>
                )}
                <button type="button" className="tutorial-manual__danger-btn" disabled={saving} onClick={openDeleteDialog}>
                  <Trash2 size={15} />
                  删除
                </button>
              </div>
            ) : null}
          </div>

          {node.summary && !editing ? (
            <div className="tutorial-page__summary">{node.summary}</div>
          ) : null}

          {editing ? (
            <div className="tutorial-editor-form">
              <div className="tutorial-editor-form__fields">
                <div className="tutorial-editor-form__fields-row">
                  <label>
                    标题
                    <input
                      value={form.title}
                      onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                    />
                  </label>
                  <LinkedNavField
                    value={form.linkedNavId}
                    onChange={(linkedNavId) => setForm((prev) => ({ ...prev, linkedNavId }))}
                  />
                </div>
                <label>
                  摘要
                  <input
                    value={form.summary}
                    onChange={(e) => setForm((prev) => ({ ...prev, summary: e.target.value }))}
                  />
                </label>
              </div>
              <div className="tutorial-editor-form__content">
                <div className="tutorial-editor-form__content-label">正文</div>
                <TutorialMarkdownEditor
                  value={form.contentMd}
                  onChange={(contentMd) => setForm((prev) => ({ ...prev, contentMd }))}
                  onUploadImage={uploadImage}
                  uploadingImage={uploadingImage}
                  defaultMode="edit"
                />
              </div>
            </div>
          ) : (
            <TutorialMarkdown content={node.contentMd} />
          )}
        </div>
        <PageOutline headings={headings} />
      </div>

      <TutorialConfirmDialog
        open={deleteOpen}
        title="确认删除"
        message={`确定删除「${node.title}」吗？此操作不可恢复。`}
        confirming={deleting}
        error={deleteError}
        onCancel={closeDeleteDialog}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

export default function TutorialManualNodePage() {
  const { slug } = useParams();
  const { isEditor, isManager, user, reloadTree } = useOutletContext() || {};
  const [node, setNode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadNode = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await tutorialAPI.getNode(slug);
      setNode(data?.node || null);
      if (data?.node?.title) {
        document.title = `${data.node.title} - IPE 使用手册`;
      }
    } catch (err) {
      setNode(null);
      setError(err.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadNode();
  }, [loadNode]);

  const handleRefresh = useCallback(async () => {
    await loadNode();
    reloadTree?.();
  }, [loadNode, reloadTree]);

  if (loading) {
    return <div className="tutorial-page"><p className="tutorial-manual__empty">加载中…</p></div>;
  }
  if (error || !node) {
    return <div className="tutorial-page"><p className="tutorial-manual__empty">{error || '内容不存在'}</p></div>;
  }

  if (node.nodeType === 'FOLDER') {
    return (
      <FolderListView
        node={node}
        user={user}
        isEditor={isEditor}
        isManager={isManager}
        onRefresh={handleRefresh}
      />
    );
  }
  return (
    <ArticleView
      node={node}
      user={user}
      isEditor={isEditor}
      isManager={isManager}
      onRefresh={handleRefresh}
    />
  );
}
